import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { activeNetworkSelector } from 'redux/selectors';
import { useLazyGetFindemSsoUrlQuery } from 'services/findemV2';
import { decodeString, buildIframeUrl } from './utils';

const useIframeMessage = (appName) => {
  const appUrls = {
    campaigns: `${import.meta.env.VITE_FINDEM_APP_NEXT_BASE_URL}/crm?tab=campaigns`,
    talentCommunities: `${import.meta.env.VITE_FINDEM_APP_NEXT_BASE_URL}/crm?tab=communities`,
    projects: `${import.meta.env.VITE_FINDEM_APP_NEXT_BASE_URL}/projects`,
    companyResearch: `${import.meta.env.VITE_FINDEM_EXEC_BASE_URL}/my-apps/company-builder?hideLayoutBars=true`,
  };
  const baseUrl = appUrls[appName];

  if (!baseUrl) {
    throw new Error(`Unknown app name: ${appName}. Valid options: ${Object.keys(appUrls).join(', ')}`);
  }

  const parsedBaseUrl = new URL(baseUrl);
  const { origin } = parsedBaseUrl;
  const baseSearchParams = parsedBaseUrl.search;

  const activeNetwork = useSelector(activeNetworkSelector);
  const [triggerGetFindemSsoUrl] = useLazyGetFindemSsoUrlQuery();
  const [iframeUrl, setIframeUrl] = useState(null);
  const [error, setError] = useState(null);

  const buildRedirectUri = () => {
    const urlParam = new URLSearchParams(window.location.search).get('url');
    if (urlParam) {
      const constructed = buildIframeUrl(origin, urlParam, baseSearchParams);
      if (constructed) return constructed;
    }
    return baseUrl;
  };

  useEffect(() => {
    if (!activeNetwork?.id) return undefined;

    let cancelled = false;
    const redirectUri = buildRedirectUri();
    const request = triggerGetFindemSsoUrl({
      collectionId: activeNetwork.id,
      redirectUri,
    });

    request.then(({ data, error: fetchError }) => {
      if (cancelled) return;

      if (data?.ssoUrl) {
        setIframeUrl(data.ssoUrl);
      } else if (fetchError) {
        setError(fetchError);
      }
    });

    return () => {
      cancelled = true;
      request.abort();
    };
  }, [activeNetwork?.id, appName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for messages from iframe to update parent URL
  useEffect(() => {
    const handleMessage = (event) => {
      // eslint-disable-next-line no-console
      console.log('[useIframeMessage] Message received', { origin: event.origin, type: event.data?.type });

      // Validate origin to prevent unauthorized URL changes from untrusted sources
      if (event.origin !== origin) {
        // eslint-disable-next-line no-console
        console.log('[useIframeMessage] Ignoring message from untrusted origin', {
          received: event.origin,
          expected: origin,
        });
        return;
      }

      if (event.data?.type === 'searchPageMsg' || event.data?.type === 'searchesPageMsg') {
        const { pathName } = event.data;

        if (pathName) {
          const url = new URL(`${window.location.origin}${window.location.pathname}`);
          url.searchParams.set('url', decodeString(pathName));
          // eslint-disable-next-line no-console
          console.log('[useIframeMessage] Updating parent URL', { pathName, newUrl: url.toString() });
          window.history.pushState({}, '', url.toString());
        } else {
          // eslint-disable-next-line no-console
          console.log('[useIframeMessage] Ignoring message — missing pathName', event.data);
        }
      } else {
        // eslint-disable-next-line no-console
        console.log('[useIframeMessage] Ignoring message — unknown type', event.data?.type);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [origin]);

  return { iframeUrl, error };
};

export default useIframeMessage;
